import React, { useReducer, useEffect } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { IntlProvider } from 'react-intl'
import withLocation from '../common/with-location'
import { useScrollTop, useSessionStorage } from '../hooks'
import { LeftMenuWrapper, LayoutWrapper, ContentWrapper, LayoutInnerWrapper } from './layout.atoms'
import Header from './header'
import NavTree from './nav-tree'
// import { Skeleton } from './skeleton'
import { NoPermission } from './no-permission'
import { GPUNav, StreamingNav } from './api-doc'
import { StateContext } from './api-doc/state-context'
import { GlobalContext } from '../common/global-context'
import { useGetInfo } from './use-get-info'

import CNJson from '../../internals/locales/jsons/cn.json'
import ENJson from '../../internals/locales/jsons/en.json'

const localeMap = {
  zh: CNJson,
  en: ENJson,
}

const initalState = {
  rightMenuList: [],
  groupIndex: 0,
  currentHash: '',
}
const reducer = (state, action) => {
  if (action.type === 'setMenu') {
    return {
      ...state,
      rightMenuList: [...state.rightMenuList, action.payload],
    }
  } else if (action.type === 'setGroupIndex') {
    return {
      ...state,
      groupIndex: action.payload,
    }
  } else if (action.type === 'setHash') {
    return {
      ...state,
      currentHash: action.payload,
    }
  } else {
    throw new Error('dispatch type does not exist')
  }
}

export const Layout = withLocation(({ children, Location }) => {
  const { hasPermission, loginName, redirect, loading } = useGetInfo()
  const { initScrollTop } = useScrollTop('left-menu')
  const [state, dispatch] = useReducer(reducer, initalState)
  const [locale, setLocale] = useSessionStorage('locale', 'zh')
  const data = useStaticQuery(graphql`
    query MyQuery {
      headerNavs: allContentfulHeaderDirectory {
        edges {
          node {
            slug
            title
            titleEn
            mainlink {
              slug
            }
            children: contentfulchildren {
              ... on ContentfulDirectories {
                slug
                title
                titleEn
                children: contentfulchildren {
                  ... on ContentfulPost {
                    slug
                    title
                    titleEn
                  }
                  ... on ContentfulDirectories {
                    slug
                    title
                    titleEn
                    children: contentfulchildren {
                      ... on ContentfulPost {
                        slug
                        title
                        titleEn
                      }
                    }
                  }
                }
              }
              ... on ContentfulPost {
                slug
                title
                titleEn
              }
            }
          }
        }
      }
    }
  `)

  useEffect(() => {
    initScrollTop()
  }, [initScrollTop])

  const { headerNavs } = data

  const parse = (children, LOCALE) => {
    return children.reduce((trees, c) => {
      if (LOCALE === 'zh' && c.title) {
        trees.push({
          slug: c.slug,
          title: c.title,
          children: c.children ? parse(c.children, LOCALE) : [], // children
        })
      } else if (LOCALE === 'en' && c.titleEn) {
        trees.push({
          slug: c.slug,
          title: c.titleEn,
          children: c.children ? parse(c.children, LOCALE) : [], // children
        })
      }
      return trees
    }, [])
  }
  const isGPU = Location.pathname.includes('gpu-instance')
  const isStreaming = Location.pathname.includes('streaming-service')

  const canAccess = isGPU
    ? hasPermission?.['gpu'] === 1
    : hasPermission?.['cloud-gaming'] === 1

  return (
    <IntlProvider messages={localeMap[locale]} locale={locale}>
      <GlobalContext.Provider value={{ locale, setLocale }}>
        <StateContext.Provider value={{ state, dispatch }}>
          <Header headerNavs={headerNavs} loginName={loginName} redirect={redirect} />
          {!loading && hasPermission !== null ? (
            <>
              {canAccess ? (
                <LayoutWrapper>
                  <LayoutInnerWrapper>
                    <LeftMenuWrapper className='left-menu'>
                      {headerNavs.edges
                        .filter(({ node }) =>
                          process.env.GATSBY_LOCALE === 'zh' ? node.title : node.titleEn,
                        )
                        .map(({ node }) => (
                          <React.Fragment key={node.slug}>
                            {Location.pathname.includes(node.slug) && (
                              <NavTree
                                tree={parse(node.children, process.env.GATSBY_LOCALE)}
                                slug={node.slug}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      {isGPU && hasPermission['gpu'] === 1 && <GPUNav />}
                      {isStreaming && hasPermission['cloud-gaming'] === 1 && <StreamingNav />}
                    </LeftMenuWrapper>
                    <ContentWrapper>{children}</ContentWrapper>
                  </LayoutInnerWrapper>
                </LayoutWrapper>
              ) : (
                <NoPermission />
              )}
            </>
          ) : null}
        </StateContext.Provider>
      </GlobalContext.Provider>
    </IntlProvider>
  )
})
